import { useMemo, useState, useEffect } from "react";
import { TemplateSelector } from "./TemplateConfigurator/TemplateSelector";
import TrebbleClipCreatorInput, { TrebbleClipCreatorInputConfig, TemplateInputConfig, VideoInputConfig } from "../remotion/TrebbleClipCreator";
import {  templateInputConfigDemo} from "../remotion/Root";
import sampleVideoData from '../remotion/video_data_to_render.json';
import { TemplateNavigationComponent } from "./TemplateConfigurator/TemplateEditor";
import { Player } from "@remotion/player";
import ConfigProvider from "../../common/ConfigProvider";
import useDefaultTrebbleAntDTokenConfig from "../../helper/useDefaultTrebbleAntDTokenConfig";
import TrebbleClientAPIHelper from "../../../models/helper/TrebbleClientAPI";
import { TrebbleClipCreatorInputSchema } from "../remotion/TrebbleClipCreatorSchema";
import TrebbleSkeleton from "../../common/TrebbleSkeleton";
import TrebbleButton from "../../common/TrebbleButton";
import { Position } from "../remotion/Components/DesignSystem";
import { BackgroundDesignSystemConfig } from "../remotion/Components/Background/Background";
import ti18n from 'i18n!nls/ClipCreatori18n';
import { parseMedia } from '@remotion/media-parser';


// Extend Window interface to declare getI18n
declare global {
  interface Window {
    getI18n: (i18nData: any, key: string, options?: any) => string;
  }
}

/** Logo Configuration */
interface LogoConfig {
  url?: string;      // URL of the logo image
  position: Position; // Position on the canvas
  size: number;       // Size relative to canvas height (e.g., percentage)
}

/** A template object is very similar to the Remotion Project Config
 *  However it excludes video rendering data
 */
export interface Template extends TemplateInputConfig {
  templateID: string;
  name: string;
  type: 'user' | 'trebble';
}


export const deleteTemplate = async (template: Template, setSavedTemplates: React.Dispatch<React.SetStateAction<Template[]>>) => {
  // FAKE API CALL
  setSavedTemplates((templates) => templates.filter((t) => t.templateID !== template.templateID));
}

const Configurator = ({ savedTemplates, setSavedTemplates, selectedTemplate, setSelectedTemplate, videoRenderData, isConfigEditing, setIsConfigEditing, workspaceId }: {
  savedTemplates: Template[],
  setSavedTemplates: React.Dispatch<React.SetStateAction<Template[]>>,
  selectedTemplate: Template,
  setSelectedTemplate: (template: Template) => void,
  videoRenderData: typeof sampleVideoData,
  isConfigEditing: boolean,
  setIsConfigEditing: (isEditing: boolean) => void,
  workspaceId: string | null | undefined
}) => {
  // Wrapper function to log when the setSelectedTemplate is called
  const debugSetSelectedTemplate = (template: Template) => {
    console.log('Configurator: setSelectedTemplate called with:', {
      templateID: template.templateID,
      showCaption: template.showCaption,
      overrideThemeCaption: template.overrideThemeCaption
    });
    setSelectedTemplate(template);
  };
 
  return (
    
      <div style={{ minWidth: "340px", maxWidth: "340px" , maxHeight: "600px"}} className="relative h-fit">
        <div
          className="border border-0 p-2 overflow-auto w-auto h-screen"
          style={{ scrollbarWidth: 'thin', height: "600px" }}
        >
          <TemplateSelector savedTemplates={savedTemplates} setSavedTemplates={setSavedTemplates} selectedTemplate={selectedTemplate} setSelectedTemplate={debugSetSelectedTemplate} isConfigEditing={isConfigEditing} workspaceId={workspaceId} />
          <TemplateNavigationComponent 
            selectedTemplate={selectedTemplate} 
            setSelectedTemplate={debugSetSelectedTemplate} 
            videoRenderData={videoRenderData} 
            setIsConfigEditing={setIsConfigEditing} 
            workspaceId={workspaceId}
          />
        </div>
      </div>
  );
}

const VideoPreview = ({ videoRenderData, template, durationInMs, framePerSec, startTimeOffset, firstScheduledNode, hideVideoPlayer, audioUrl }: { videoRenderData: typeof sampleVideoData, template: Template , durationInMs:number, framePerSec:number,  startTimeOffset: number,firstScheduledNode: any, hideVideoPlayer: boolean, audioUrl: string}) => {
  const [actualFrameRate, setActualFrameRate] = useState(framePerSec);
  const templateConfig = useDefaultTrebbleAntDTokenConfig({useEditorTheme: true});

  useEffect(() => {
    const analyzeVideos = async () => {
      try {
        // Get unique video URLs from all segments
        const uniqueVideoUrls = new Set(
          videoRenderData.map(segment => {
            const url = segment.sequencerNodeReference?.audioSegment?.audioUrl || segment.cachedBlobUrl;
            return url;
          }).filter(Boolean)
        );

        // Analyze each unique video
        const metadataPromises = Array.from(uniqueVideoUrls).map(url => 
          parseMedia({
            src: url,
            fields: {
              fps: true
            }
          })
        );

        const metadataResults = await Promise.all(metadataPromises);
        
        // Find the maximum frame rate among all videos
        const frameRates = metadataResults.map(m => m.fps).filter((fps): fps is number => fps !== null);
        if (frameRates.length > 0) {
          const maxFrameRate = Math.max(...frameRates);
          setActualFrameRate(maxFrameRate);
        }

      } catch (error) {
        console.error('Error analyzing videos:', error);
      }
    };

    analyzeVideos();
  }, [videoRenderData]);

  const trebbleClipCreatorInput = useMemo(() => ({
    templateInputConfig: {...template},
    videoInputConfig: { 
      audioUrl,
      videoRenderData: videoRenderData,
      startTimeOffset, 
      firstScheduledNode,
      durationInMs,
      framePerSec: actualFrameRate,
      inCloudRenderingEnvironment: false
    }
  }), [template, videoRenderData, startTimeOffset, firstScheduledNode, audioUrl, durationInMs, actualFrameRate]);

  // Rollbar #1596/#1612: Remotion Player throws 'compositionHeight must be a number'
  // when designSystem.canvas.height is not a number (bad/partial config).
  const rawCanvasHeight = trebbleClipCreatorInput?.templateInputConfig?.designSystem?.canvas?.height;
  const rawCanvasWidth = trebbleClipCreatorInput?.templateInputConfig?.designSystem?.canvas?.width;
  const safeCanvasHeight = typeof rawCanvasHeight === 'number' && rawCanvasHeight > 0 ? rawCanvasHeight : 1080;
  const safeCanvasWidth = typeof rawCanvasWidth === 'number' && rawCanvasWidth > 0 ? rawCanvasWidth : 1920;

  return (
    <div
      style={{
        width: '100%',
        height: '500px',
        maxHeight: '100vh',
        backgroundColor: templateConfig.token.colorBgElevated,
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        aspectRatio: `${safeCanvasWidth} / ${safeCanvasHeight}`
      }}
    >
      <div style={{
        maxHeight: '100%',
        maxWidth: '100%',
        aspectRatio: `${safeCanvasWidth} / ${safeCanvasHeight}`
      }}>
        {!hideVideoPlayer && <Player
          style={{ width: '100%' }}
          component={TrebbleClipCreatorInput}
          compositionHeight={safeCanvasHeight}
          compositionWidth={safeCanvasWidth}
          fps={actualFrameRate}
          durationInFrames={Math.ceil(durationInMs * actualFrameRate / 1000)}
          inputProps={trebbleClipCreatorInput as TrebbleClipCreatorInputSchema}
          controls
        />}
      </div>
    </div>
  );
}

export const createDefaultTemplate = (): Template => ({
  ...templateInputConfigDemo,
  templateID: '1',
  type: "trebble" as const,
  name: window.getI18n(ti18n, "DEFAULT_TEMPLATE_NAME"),
});

const fetchTemplatesFromAPI = async (workspaceId: string | null | undefined): Promise<Template[]> => {
  try {
    const templates = await TrebbleClientAPIHelper.getInstance().getTemplates({}, workspaceId);
    return templates && templates.length > 0 ? templates : [createDefaultTemplate()];
  } catch (error) {
    console.error('Error fetching templates:', error);
    return [createDefaultTemplate()];
  }
};

export const VideoEditor = ({ videoRenderData, durationInMs, framePerSec, startTimeOffset, firstScheduledNode, audioUrl, onGenerateClipButtonClicked, videoTierResolution, workspaceId}: {
  videoRenderData: typeof sampleVideoData,
  durationInMs: number, 
  framePerSec: number, 
  startTimeOffset: number,
  firstScheduledNode: any,
  audioUrl: string,
  videoTierResolution: string,
  workspaceId: string | null | undefined,
  onGenerateClipButtonClicked: (config: TrebbleClipCreatorInputConfig) => Promise<void>
}) => {
  const [savedTemplates, setSavedTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template>(createDefaultTemplate());
  const [videoBeingGenerated, setVideoBeingGenerated] = useState<boolean>(false);
  const [isConfigEditing, setIsConfigEditing] = useState<boolean>(false);
  const themeConfig = useDefaultTrebbleAntDTokenConfig({useEditorTheme: true});
  const token = useDefaultTrebbleAntDTokenConfig();

  useEffect(() => {
    const initializeTemplates = async () => {
      const templates = await fetchTemplatesFromAPI(workspaceId);
      setSavedTemplates(templates);
      setSelectedTemplate(templates[0]);
    };
    initializeTemplates();
  }, [workspaceId]);

  const handleTemplateChange = (newTemplate: Template) => {
    console.log("handleTemplateChange called with:", {
      templateID: newTemplate.templateID,
      showCaption: newTemplate.showCaption,
      overrideThemeCaption: newTemplate.overrideThemeCaption
    });
    setSelectedTemplate(newTemplate);
  };

  const onCreateButtonClicked = async () => {
    setVideoBeingGenerated(true);
    try {
      await onGenerateClipButtonClicked({
        templateInputConfig: {...selectedTemplate},
        videoInputConfig: {
          videoRenderData,
          durationInMs,
          framePerSec,
          startTimeOffset,
          firstScheduledNode,
          audioUrl,
          inCloudRenderingEnvironment: true,
          videoTierResolution
        }
      });
      setVideoBeingGenerated(false);
    } catch (error) {
      console.error(error);
      setVideoBeingGenerated(false);
    }
  };

  if (!selectedTemplate || savedTemplates.length === 0) {
    // Loading skeleton for the whole editor
    return (
      <div className="w-full" style={{ height: '600px', background: token.token.colorBgElevated, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 16, height: 550, width: '100%', paddingLeft: '75px' }}>
          {/* Sidebar skeleton */}
          <div style={{ minWidth: 340, maxWidth: 340, padding: 16, background: token.token.colorBgElevated, height: 500, borderRadius: 0, marginTop: 24 }}>
            <TrebbleSkeleton.Input active style={{ width: '100%', height: 48, marginBottom: 24 }} />
            <TrebbleSkeleton active paragraph={{ rows: 8, width: '100%' }} title={false} />
          </div>
          {/* Main area skeleton */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', padding: 16 }}>
              <TrebbleSkeleton.Button active style={{ width: 120, height: 40 }} />
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <TrebbleSkeleton.Image active style={{ width: 640, height: 360, borderRadius: 0 }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ConfigProvider useEditorTheme={true} theme={themeConfig}>
      <div className={`flex flex-row items-start w-full gap-4 max-h-screen`} style={{backgroundColor: themeConfig.token.colorBgElevated}}>
        <Configurator 
          savedTemplates={savedTemplates} 
          setSavedTemplates={setSavedTemplates} 
          selectedTemplate={selectedTemplate} 
          setSelectedTemplate={handleTemplateChange} 
          videoRenderData={videoRenderData} 
          isConfigEditing={isConfigEditing}
          setIsConfigEditing={setIsConfigEditing}
          workspaceId={workspaceId}
        />
        <div className="flex flex-col w-full">
          <div className="flex flex-col items-end p-2">
            <TrebbleButton type="primary" onClick={onCreateButtonClicked} loading={videoBeingGenerated}>
              {videoBeingGenerated ? window.getI18n(ti18n, "EXPORTING_BUTTON_TEXT") : window.getI18n(ti18n, "EXPORT_BUTTON_TEXT")}
            </TrebbleButton>
          </div>
          <VideoPreview 
            videoRenderData={videoRenderData} 
            audioUrl={audioUrl} 
            template={selectedTemplate} 
            durationInMs={durationInMs} 
            framePerSec={framePerSec} 
            startTimeOffset={startTimeOffset} 
            firstScheduledNode={firstScheduledNode} 
            hideVideoPlayer={videoBeingGenerated}
          />
        </div>
      </div>
    </ConfigProvider>
  );
};
